// Xem PDF biên bản thỏa thuận đã lưu
import MessageBox from "components/shared-components/MessageBox";
import { isAgreementPdfReady } from "hooks/invoiceAgreement/useAgreementPdfUrl";
import ReactPdfViewer from "components/shared-components/ReactPdfViewer";
import {
  AGREEMENT_PDF_STATUS_GENERATED,
  AGREEMENT_REPORT_TITLE,
} from "constants/AgreementReportConstant";
import {
  useDownloadAgreement,
  useGetAgreementInfo,
} from "hooks/invoiceAgreement/useInvoiceAgreement";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, Spinner } from "reactstrap";

// Sau khi lưu, backend tạo file PDF bất đồng bộ nên phải hỏi lại tới khi xong
const DELAY_CHECK_PDF = 3000;
const MAX_CHECK_PDF = 10;

const AgreementPdfModal = props => {
  const { isOpen, toggle, invoiceId, typeReport, agreementId, agreementInfo } =
    props;

  const [pdfUrl, setPdfUrl] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isWaitTooLong, setIsWaitTooLong] = useState(false);

  const checkCountRef = useRef(0);
  const timerRef = useRef(null);

  const { mutate: mutateDownloadAgreement } = useDownloadAgreement({
    onSuccess: response => {
      setPdfUrl(URL.createObjectURL(response.data));
    },
    onError: () => {
      setIsError(true);
    },
  });

  const { mutate: mutateGetAgreementInfo } = useGetAgreementInfo({
    onSuccess: response => {
      const agreementData = response.data;

      // Đã tạo xong file thì tải về hiển thị
      if (
        agreementData?.pdf_status === AGREEMENT_PDF_STATUS_GENERATED &&
        agreementData?.agreement_id
      ) {
        mutateDownloadAgreement({ agreement_id: agreementData.agreement_id });
        return;
      }

      // Chưa xong: chờ rồi hỏi lại, quá số lần cho phép thì để user tự thử lại
      if (checkCountRef.current >= MAX_CHECK_PDF) {
        setIsWaitTooLong(true);
        return;
      }

      checkCountRef.current += 1;
      timerRef.current = setTimeout(() => {
        mutateGetAgreementInfo({
          id: invoiceId,
          type: typeReport,
          agreement_id: agreementId,
        });
      }, DELAY_CHECK_PDF);
    },
    onError: () => {
      setIsError(true);
    },
  });

  const startCheckPdf = () => {
    clearTimeout(timerRef.current);
    checkCountRef.current = 0;
    setPdfUrl(null);
    setIsError(false);
    setIsWaitTooLong(false);
    mutateGetAgreementInfo({
      id: invoiceId,
      type: typeReport,
      agreement_id: agreementId,
    });
  };

  // Mở modal thì hỏi trạng thái file, đóng modal thì xóa sạch
  useEffect(() => {
    if (isOpen && invoiceId) {
      // Modal cha vừa nạp lại biên bản mà PDF đã xong thì tải luôn, khỏi gọi
      // agreement-info lần nữa. "Thử lại" vẫn hỏi lại API như cũ.
      if (isAgreementPdfReady(agreementInfo)) {
        mutateDownloadAgreement({ agreement_id: agreementInfo.agreement_id });
        return;
      }
      startCheckPdf();
    } else {
      clearTimeout(timerRef.current);
      setPdfUrl(null);
      setIsError(false);
      setIsWaitTooLong(false);
    }
  }, [isOpen]);

  // Dừng hẹn giờ khi component bị gỡ
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  // Thu hồi object URL cũ để không rò bộ nhớ
  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const styleDiv = {
    width: "100%",
    height: "90vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#8080802b",
  };

  return (
    <Modal
      size="xl"
      contentClassName="confirm-modal"
      isOpen={isOpen}
      role="dialog"
      autoFocus={true}
      centered={true}
      className="exampleModal"
      toggle={toggle}
    >
      <div className="modal-content">
        <ModalBody>
          {isError ? (
            <div style={styleDiv}>
              <MessageBox
                variant="danger"
                classMore="d-sm-flex align-items-center justify-content-center gap-1 font-size-18"
              >
                <i className="bx bxs-error-alt"></i>Không tải được file biên bản
              </MessageBox>
            </div>
          ) : isWaitTooLong ? (
            <div style={styleDiv}>
              <MessageBox
                variant="warning"
                classMore="d-sm-flex align-items-center justify-content-center gap-1 font-size-18"
              >
                <i className="bx bx-time-five"></i>File biên bản đang được tạo,
                vui lòng thử lại
              </MessageBox>
            </div>
          ) : !pdfUrl ? (
            <div style={styleDiv}>
              <div className="d-flex flex-column align-items-center gap-2">
                <Spinner color="primary" />
                <span>Đang tạo file biên bản...</span>
              </div>
            </div>
          ) : (
            <ReactPdfViewer filePdfUrl={pdfUrl} style={{ height: "80vh" }} />
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="danger"
            className="me-2"
            onClick={toggle}
          >
            Đóng
          </Button>
          {pdfUrl && (
            <Button
              type="button"
              color="success"
              className="me-2 btn-label"
              onClick={() => {
                const link = document.createElement("a");
                link.href = pdfUrl;
                link.setAttribute(
                  "download",
                  `${AGREEMENT_REPORT_TITLE[typeReport] || "bien-ban"}.pdf`
                );
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              <i className="bx bx-download label-icon" />
              Tải PDF
            </Button>
          )}
          {(isWaitTooLong || isError) && (
            <Button
              type="button"
              color="primary"
              className="me-2 btn-label"
              onClick={startCheckPdf}
            >
              <i className="bx bx-refresh label-icon"></i>
              Thử lại
            </Button>
          )}
        </ModalFooter>
      </div>
    </Modal>
  );
};

AgreementPdfModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  invoiceId: PropTypes.number,
  typeReport: PropTypes.string,
  // Biên bản đang xem (một loại lập được nhiều lần)
  agreementId: PropTypes.number,
  // Biên bản modal cha vừa nạp (agreement-info), có thì bỏ qua lần gọi lại
  agreementInfo: PropTypes.object,
};

export default AgreementPdfModal;
